import { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import { useTranslation } from 'react-i18next';
import type { Designation } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';

/** Format a decimal-hours value as "X.X h". */
function fmtH(hours: number): string {
  return `${hours.toFixed(1)} h`;
}

const STATUS_COLORS: Record<string, string> = {
  ordered: colors.softGreen,
  pending: colors.lightGray,
  'on-hold': colors.warmBeige,
  closed: colors.lightGray,
};

interface Props {
  designations: Array<{ id: string; data: Designation }>;
}

/**
 * Budget overview panel shown at the top of the per-project Kronos tab.
 * Mirrors Clockify's "Status" view: global KPIs + donut + per-designation
 * progress breakdown.
 *
 * Reads `hoursSold` / `hoursSpent` / `percentCompletion` directly from the
 * designation documents — these are computed and stored by the Cloud
 * Function `onTimeEntryWrite` so there is no extra Firestore subscription
 * needed here.
 *
 * A line is shown when it was **sold** or when it was **worked**, because the
 * two live on different documents: the Clockify migration parked 131 756 h on
 * `clk_*` lines that no budget ever sold, while the `bud_*` lines the budget
 * import created carry `hoursSold` and no hours. Filtering on `hoursSold > 0`
 * showed "Saisi 0.0 h" on projects with hundreds of real hours. Likewise the
 * billing mode is not a filter: a forfait sells no hours but time can be
 * logged against it, and 177 of the 245 production projects carry hours with
 * nothing sold at all — hence `hasBudget`, which drops the budget-progress
 * reading rather than show a meaningless 0 %.
 */
export default function KronosSummaryPanel({ designations }: Props) {
  const { t } = useTranslation();

  const shown = useMemo(
    () =>
      designations.filter(
        (d) => (d.data.hoursSold ?? 0) > 0 || (d.data.hoursSpent ?? 0) > 0,
      ),
    [designations],
  );

  // Only an hourly line sells hours — same rule as `projectSummary` in
  // finance.ts, so the Budget tab and this panel cannot disagree.
  const totalEstimate = useMemo(
    () =>
      shown.reduce(
        (sum, d) => sum + (d.data.billingMode === 'fixed' ? 0 : (d.data.hoursSold ?? 0)),
        0,
      ),
    [shown],
  );

  const totalSpent = useMemo(
    () => shown.reduce((sum, d) => sum + (d.data.hoursSpent ?? 0), 0),
    [shown],
  );

  // Nothing to show if no designation was sold or worked.
  if (shown.length === 0) return null;

  const hasBudget = totalEstimate > 0;
  const overage = Math.max(0, totalSpent - totalEstimate);
  const pct = hasBudget ? (totalSpent / totalEstimate) * 100 : 0;
  const isOver = pct > 100;

  // Donut: budget vs. consumed, or if over budget: budget + overage slice.
  // With no budget there is nothing to compare to, so it shows the hours
  // alone — `remaining` would otherwise go negative and break the chart.
  const pieData = !hasBudget
    ? [{ id: 'spent', value: totalSpent, color: colors.kronosAccent }]
    : isOver
      ? [
          { id: 'estimate', value: totalEstimate, color: colors.kronosAccent },
          { id: 'overage', value: overage, color: colors.red },
        ]
      : totalSpent > 0
        ? [
            { id: 'spent', value: totalSpent, color: colors.kronosAccent },
            {
              id: 'remaining',
              value: Math.max(0, totalEstimate - totalSpent),
              color: colors.lightGray,
            },
          ]
        : [{ id: 'empty', value: totalEstimate, color: colors.lightGray }];

  // Progress bar caps at 100% visually but percentage label shows the real value.
  const barWidthPct = Math.min(pct, 100);
  const barColor = isOver ? colors.red : colors.kronosAccent;

  return (
    <Box sx={{ mb: 3 }}>
      {/* ── Main summary card ── */}
      <Paper variant="outlined" sx={{ borderColor: colors.lightGray, borderRadius: 0, p: 3 }}>
        {/* Global progress bar — only where a budget exists to progress against */}
        {hasBudget && (
        <Box sx={{ mb: 3 }} data-testid="kronos-summary-progress">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('kronos.projectTab.summary.progressLabel')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700 }}
              color={isOver ? 'error.main' : 'text.primary'}
            >
              {pct.toFixed(2)}%
            </Typography>
          </Box>
          <Box
            sx={{
              height: 6,
              bgcolor: colors.lightGray,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${barWidthPct}%`,
                bgcolor: barColor,
                transition: 'width 0.4s ease',
              }}
            />
          </Box>
        </Box>
        )}

        {/* Two-column layout: KPIs left, donut right */}
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Left: stat cards */}
          <Stack spacing={2.5} sx={{ minWidth: 160 }}>
            {hasBudget && (
              <Box data-testid="kronos-summary-estimate">
                <Typography variant="caption" color="text.secondary">
                  {t('kronos.projectTab.summary.estimate')}
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.25 }}>
                  {fmtH(totalEstimate)}
                </Typography>
              </Box>
            )}
            <Box data-testid="kronos-summary-tracked">
              <Typography variant="caption" color="text.secondary">
                {t('kronos.projectTab.summary.tracked')}
              </Typography>
              <Typography variant="h3" sx={{ mt: 0.25 }}>
                {fmtH(totalSpent)}
              </Typography>
            </Box>
            {isOver && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('kronos.projectTab.summary.overage')}
                </Typography>
                <Typography variant="h3" color="error.main" sx={{ mt: 0.25 }}>
                  +{fmtH(overage)}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Right: donut chart */}
          <Box
            sx={{
              flex: '1 1 220px',
              position: 'relative',
              height: 220,
              maxWidth: 340,
            }}
          >
            <PieChart
              height={220}
              margin={{ left: 0, right: 0, top: 0, bottom: 0 }}
              series={[
                {
                  data: pieData,
                  innerRadius: 65,
                  outerRadius: 100,
                  paddingAngle: 1,
                  cornerRadius: 0,
                },
              ]}
              slotProps={{ legend: { sx: { display: 'none' } } }}
            />
            {/* Center label */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography sx={{ fontSize: 20, fontWeight: 900, lineHeight: 1 }}>
                {fmtH(totalSpent)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('kronos.projectTab.summary.tracked')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* ── Per-designation breakdown ── */}
      <Paper
        variant="outlined"
        sx={{ borderColor: colors.lightGray, borderRadius: 0, mt: 1 }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${colors.lightGray}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('kronos.projectTab.summary.breakdown')}
          </Typography>
        </Box>

        {/* Column headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 100px 140px 220px' },
            px: 2,
            py: 1,
            borderBottom: `1px solid ${colors.lightGray}`,
            gap: 2,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {t('kronos.projectTab.summary.colDesignation')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('kronos.projectTab.summary.colStatus')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
            {t('kronos.projectTab.summary.colHours')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('kronos.projectTab.summary.colProgress')}
          </Typography>
        </Box>

        {/* Rows */}
        {shown.map(({ id, data: d }) => {
          const spent = d.hoursSpent ?? 0;
          const sold = d.hoursSold ?? 0;
          const rowPct = sold > 0 ? (spent / sold) * 100 : 0;
          const rowOver = spent > sold;
          const barFill = Math.min(rowPct, 100);

          return (
            <Box
              key={id}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 100px 140px 220px' },
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${colors.lightGray}`,
                gap: 2,
                alignItems: 'center',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              {/* Name + section */}
              <Box>
                <Typography variant="body2">{d.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`projects.designations.sections.${d.section}`, d.section)}
                </Typography>
              </Box>

              {/* Status chip */}
              <Chip
                label={t(`finance.status.${d.status}`, d.status)}
                size="small"
                sx={{
                  bgcolor: STATUS_COLORS[d.status] ?? colors.lightGray,
                  color: colors.bodyText,
                  borderRadius: 0,
                  height: 22,
                  fontSize: 11,
                  justifySelf: 'start',
                }}
              />

              {/* Hours: spent / sold — a line that was worked but never sold
                  has no denominator, so it reads "22.5 h / —". */}
              <Typography
                variant="body2"
                sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                data-testid={`kronos-summary-hours-${id}`}
              >
                {fmtH(spent)}
                <Typography component="span" variant="caption" color="text.secondary">
                  {' / '}{sold > 0 ? fmtH(sold) : '—'}
                </Typography>
              </Typography>

              {/* Progress bar + % */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    height: 6,
                    bgcolor: colors.lightGray,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${barFill}%`,
                      bgcolor: rowOver ? colors.red : colors.kronosAccent,
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  color={rowOver ? 'error.main' : 'text.secondary'}
                  sx={{ minWidth: 48, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  {sold > 0 ? `${rowPct.toFixed(0)}%` : '—'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
}
